import type { HackcalResponse } from "./types";

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTH_LABELS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * hackcal date keys ("2026-08-04") must be parsed as local wall-clock components, not
 * via `new Date(key)` - the bare ISO date form is parsed as UTC midnight by spec, which
 * lands on the previous day in any timezone behind UTC (e.g. the Americas), potentially
 * opening the calendar on the wrong month for those visitors.
 */
function parseDateKey(key: string): Date {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** Monday = 0 ... Sunday = 6, so the grid can start the week on Monday. */
function mondayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

export interface CalendarOptions {
  onSelectDay?: (dateKey: string) => void;
}

export class Calendar {
  private container: HTMLElement;
  private data: HackcalResponse;
  private viewYear: number;
  private viewMonth: number;
  private options: CalendarOptions;

  constructor(container: HTMLElement, data: HackcalResponse, options: CalendarOptions = {}) {
    this.container = container;
    this.data = data;
    this.options = options;

    const eventDates = Object.keys(data).sort();
    const initialDate = eventDates.length > 0 ? parseDateKey(eventDates[0]) : new Date();
    this.viewYear = initialDate.getFullYear();
    this.viewMonth = initialDate.getMonth();

    this.render();
  }

  updateData(data: HackcalResponse): void {
    this.data = data;
    this.render();
  }

  get currentLabel(): string {
    return `${MONTH_LABELS[this.viewMonth]} ${this.viewYear}`;
  }

  private navigate(delta: number): void {
    let month = this.viewMonth + delta;
    let year = this.viewYear;
    if (month < 0) {
      month = 11;
      year -= 1;
    } else if (month > 11) {
      month = 0;
      year += 1;
    }
    this.viewMonth = month;
    this.viewYear = year;
    this.render();
  }

  private render(): void {
    this.container.replaceChildren();

    const header = document.createElement("div");
    header.className = "calendar-header";

    const prevBtn = document.createElement("button");
    prevBtn.type = "button";
    prevBtn.className = "calendar-nav-btn";
    prevBtn.setAttribute("aria-label", "Vorheriger Monat");
    prevBtn.textContent = "‹";
    prevBtn.addEventListener("click", () => this.navigate(-1));

    const title = document.createElement("span");
    title.className = "calendar-title";
    title.textContent = this.currentLabel;

    const nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "calendar-nav-btn";
    nextBtn.setAttribute("aria-label", "Nächster Monat");
    nextBtn.textContent = "›";
    nextBtn.addEventListener("click", () => this.navigate(1));

    header.append(prevBtn, title, nextBtn);

    const weekdayRow = document.createElement("div");
    weekdayRow.className = "calendar-weekdays";
    weekdayRow.setAttribute("aria-hidden", "true");
    for (const label of WEEKDAY_LABELS) {
      const cell = document.createElement("span");
      cell.className = "calendar-weekday";
      cell.textContent = label;
      weekdayRow.append(cell);
    }

    const grid = document.createElement("div");
    grid.className = "calendar-grid";
    grid.setAttribute("role", "grid");
    grid.setAttribute("aria-label", `Terminkalender ${this.currentLabel}`);

    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const leadingBlanks = mondayIndex(firstOfMonth);
    const totalDays = daysInMonth(this.viewYear, this.viewMonth);
    const todayKey = toDateKey(new Date());

    // The "grid" role requires its gridcells to be inside "row" children (WAI-ARIA
    // grid pattern) - a plain flat list of gridcells fails aria-required-children/
    // aria-required-parent. Rows are `display: contents` so they stay invisible to
    // the CSS grid layout; only the ARIA tree gains the row level it needs.
    let row: HTMLElement | null = null;
    const appendCell = (cell: HTMLElement): void => {
      if (!row || row.childElementCount === 7) {
        row = document.createElement("div");
        row.className = "calendar-row";
        row.setAttribute("role", "row");
        grid.append(row);
      }
      row.append(cell);
    };

    for (let i = 0; i < leadingBlanks; i++) {
      const blank = document.createElement("span");
      blank.className = "calendar-day calendar-day-empty";
      blank.setAttribute("aria-hidden", "true");
      appendCell(blank);
    }

    const interactiveButtons: HTMLButtonElement[] = [];

    for (let day = 1; day <= totalDays; day++) {
      const dateKey = toDateKey(new Date(this.viewYear, this.viewMonth, day));
      const dayData = this.data[dateKey];

      const cell = document.createElement("button");
      cell.type = "button";
      cell.className = "calendar-day";
      cell.textContent = String(day);
      cell.setAttribute("role", "gridcell");
      cell.tabIndex = -1;

      if (dateKey === todayKey) cell.classList.add("is-today");

      if (dayData) {
        cell.classList.add("has-events");
        const dot = document.createElement("span");
        dot.className = "calendar-day-dot";
        dot.setAttribute("aria-hidden", "true");
        cell.append(dot);
        const eventCount = Object.keys(dayData.events).length;
        cell.setAttribute(
          "aria-label",
          `${day}. ${MONTH_LABELS[this.viewMonth]}, ${eventCount} Termin${eventCount === 1 ? "" : "e"}`,
        );
        cell.addEventListener("click", () => this.options.onSelectDay?.(dateKey));
        interactiveButtons.push(cell);
      } else {
        cell.disabled = true;
        cell.classList.add("is-empty-day");
      }

      appendCell(cell);
    }

    interactiveButtons.forEach((btn, index) => {
      btn.tabIndex = index === 0 ? 0 : -1;
      btn.addEventListener("keydown", (event) => {
        let targetIndex: number;
        switch (event.key) {
          case "ArrowRight":
            targetIndex = index + 1;
            break;
          case "ArrowLeft":
            targetIndex = index - 1;
            break;
          case "Home":
            targetIndex = 0;
            break;
          case "End":
            targetIndex = interactiveButtons.length - 1;
            break;
          default:
            return;
        }
        event.preventDefault();
        const target = interactiveButtons[(targetIndex + interactiveButtons.length) % interactiveButtons.length];
        interactiveButtons.forEach((b) => (b.tabIndex = -1));
        target.tabIndex = 0;
        target.focus();
      });
    });

    // Animate the day grid only: re-running it on the header too would make the
    // month title flicker on every prev/next click.
    grid.classList.add("calendar-grid-enter");

    this.container.append(header, weekdayRow, grid);
  }
}
